// src/pages/LoginPage.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, TextField, Button, Typography,
  InputAdornment, IconButton, Alert, CircularProgress, Divider, Switch,
} from '@mui/material';
import {
  Visibility, VisibilityOff, Lock, Person, Download, Android, Apple, AdminPanelSettings, PhoneAndroid,
} from '@mui/icons-material';
import { login, clearError, selectAuthLoading, selectAuthError, selectToken, selectUser } from '../store/authSlice';

const APK_URL = 'https://drive.google.com/file/d/1pHLlurwRUxsWXwrhodROr5QJUD6fgHiX/view?usp=sharing';
const APK_QR = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(APK_URL)}&bgcolor=ffffff&color=000000&margin=6`;

const TESTFLIGHT_URL = 'https://testflight.apple.com/join/VXtRGDdJ';
const TESTFLIGHT_QR = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(TESTFLIGHT_URL)}&bgcolor=ffffff&color=0f2137&margin=6`;

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const token = useSelector(selectToken);
  const user = useSelector(selectUser);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  // false = show downloads (default), true = show admin login
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  useEffect(() => {
    if (token && user?.role?.canAccessDasboard) navigate('/dashboard', { replace: true });
  }, [token, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());
    const result = await dispatch(login({ username, password }));
    if (login.fulfilled.match(result)) {
      const u = result.payload.user;
      const isAdmin = u?.role?.canAccessDasboard === true;
      if (!isAdmin) {
        dispatch({ type: 'auth/logout' });
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        dispatch({ type: 'auth/login/rejected', payload: 'Access denied. Admin privileges required.' });
        return;
      }
      navigate('/dashboard', { replace: true });
    }
  };

  const QRBlock = ({ src, alt, label }) => (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box sx={{
        p: 1, borderRadius: 2,
        border: '1px solid rgba(16,117,186,0.15)',
        background: '#fff',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>
        <img src={src} alt={alt} width={100} height={100} style={{ display: 'block', borderRadius: 6 }} />
      </Box>
      <Typography variant="caption" sx={{ color: '#8aacbf', fontSize: '0.7rem' }}>{label}</Typography>
    </Box>
  );

  const PlatformSection = ({
    icon, iconBg, iconShadow, title, subtitle,
    qrSrc, qrAlt, qrLabel,
    buttonLabel, buttonHref, buttonDownload,
    buttonBorderColor, buttonColor, buttonHoverBg, buttonHoverBorder,
    version,
  }) => (
    <Box sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 1.5,
      px: { xs: 1, md: 2 },
    }}>
      <Box sx={{
        width: 56, height: 56, borderRadius: '16px',
        background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: iconShadow,
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f2137', lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: '#4a6a85', fontSize: '0.7rem' }}>
          {subtitle}
        </Typography>
      </Box>
      <QRBlock src={qrSrc} alt={qrAlt} label={qrLabel} />
      <Button
        variant="outlined"
        size="small"
        startIcon={buttonDownload ? <Download sx={{ fontSize: 16 }} /> : <Apple sx={{ fontSize: 16 }} />}
        href={buttonHref}
        download={buttonDownload}
        target={buttonDownload ? undefined : '_blank'}
        rel={buttonDownload ? undefined : 'noopener noreferrer'}
        sx={{
          py: 1, px: 2, borderRadius: 2,
          borderColor: buttonBorderColor,
          color: buttonColor,
          fontWeight: 600,
          fontSize: '0.78rem',
          whiteSpace: 'nowrap',
          '&:hover': { borderColor: buttonHoverBorder, background: buttonHoverBg },
        }}
      >
        {buttonLabel}
      </Button>
      <Typography variant="caption" sx={{ color: '#a0b8cc', fontSize: '0.68rem' }}>
        {version}
      </Typography>
    </Box>
  );

  // The downloads content — shared between mobile and desktop
  const DownloadsContent = () => (
    <>
      <Typography variant="caption" sx={{ color: '#6b8fa8', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
        Download the App
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, width: '100%', gap: 0 }}>
        <PlatformSection
          icon={<Android sx={{ color: '#fff', fontSize: 30 }} />}
          iconBg="linear-gradient(135deg, #000000 0%, #000000 100%)"
          iconShadow="0 6px 20px rgba(16,117,186,0.28)"
          title="Android"
          subtitle="Direct APK download"
          qrSrc={APK_QR}
          qrAlt="Android APK QR Code"
          qrLabel="Scan to download"
          buttonLabel="Download APK"
          buttonHref={APK_URL}
          buttonDownload={true}
          buttonBorderColor="rgba(16,117,186,0.4)"
          buttonColor="#000000"
          buttonHoverBg="rgba(16,117,186,0.06)"
          buttonHoverBorder="#000000"
          version="Android 8.0+"
        />
        <Divider orientation="horizontal" flexItem sx={{ display: { xs: 'block', md: 'none' }, borderColor: 'rgb(114 186 16 / 12%)', my: 2 }} />
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: 'rgb(114 186 16 / 12%)', mx: 1 }} />
        <PlatformSection
          icon={<Apple sx={{ color: '#fff', fontSize: 30 }} />}
          iconBg="linear-gradient(135deg, #1c1c1e 0%, #3a3a3c 100%)"
          iconShadow="0 6px 20px rgba(0,0,0,0.2)"
          title="iOS (TestFlight)"
          subtitle="Beta via TestFlight"
          qrSrc={TESTFLIGHT_QR}
          qrAlt="TestFlight QR Code"
          qrLabel="Scan to open"
          buttonLabel="Open TestFlight"
          buttonHref={TESTFLIGHT_URL}
          buttonDownload={false}
          buttonBorderColor="rgba(28,28,30,0.3)"
          buttonColor="#1c1c1e"
          buttonHoverBg="rgba(0,0,0,0.04)"
          buttonHoverBorder="#1c1c1e"
          version="iOS 15+"
        />
      </Box>
    </>
  );

  // The login form content
  const LoginContent = () => (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f2137', mb: 0.5 }}>
          Admin Console
        </Typography>
        <Typography variant="body2" sx={{ color: '#4a6a85' }}>
          Sign in to access the management panel
        </Typography>
      </Box>
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {error && (
            <Alert severity="error" sx={{
              borderRadius: 2,
              background: 'rgba(244,67,54,0.08)',
              border: '1px solid rgba(244,67,54,0.25)',
              color: '#d32f2f',
              '& .MuiAlert-icon': { color: '#f44336' },
            }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required fullWidth autoComplete="username"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: '#4a6a85', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Password"
            type={showPass ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: '#4a6a85', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPass(!showPass)} edge="end" size="small">
                    {showPass
                      ? <VisibilityOff sx={{ color: '#4a6a85', fontSize: 20 }} />
                      : <Visibility sx={{ color: '#4a6a85', fontSize: 20 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth size="large"
            disabled={loading}
            sx={{ mt: 0.5, py: 1.5, fontSize: '1rem' }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign In'}
          </Button>
        </Box>
      </form>
      <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#6b8fa8' }}>
        Restricted access — authorized administrators only
      </Typography>
    </>
  );

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      background: 'radial-gradient(ellipse at 30% 20%, rgba(16,117,186,0.15) 0%, transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(0,212,170,0.08) 0%, transparent 50%), #fff',
    }}>
      {/* Decorative grid */}
      <Box sx={{
        position: 'absolute', inset: 0, opacity: 0.04,
        backgroundImage: 'linear-gradient(rgba(16,117,186,1) 1px, transparent 1px), linear-gradient(90deg, rgba(16,117,186,1) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
        pointerEvents: 'none',
      }} />
      {/* Glowing orbs */}
      <Box sx={{
        position: 'absolute', width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgb(114 186 16 / 12%) 0%, transparent 70%)',
        top: '-10%', left: '-5%', pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', width: 300, height: 300, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,212,170,0.08) 0%, transparent 70%)',
        bottom: '5%', right: '5%', pointerEvents: 'none',
      }} />

      <Box sx={{ width: '100%', maxWidth: 1020, px: 2, position: 'relative', zIndex: 1 }}>
        <Card sx={{
          background: '#ffffff',
          border: '1px solid rgba(16,117,186,0.15)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.12), 0 0 0 1px rgba(16,117,186,0.05)',
          borderRadius: 3,
          overflow: 'hidden',
        }}>

          {/* ── MOBILE LAYOUT ── */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column' }}>

            {/* Logo + toggle row */}
            <Box sx={{
              px: 4, pt: 4, pb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Box sx={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="logo.png" style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="Logo" />
              </Box>

              {/* Toggle */}
             
            </Box>

            {/* Toggle label */}
            {/* <Typography variant="caption" sx={{
              textAlign: 'center', pb: 2,
              color: '#6b8fa8', fontSize: '0.72rem',
            }}>
              {showAdminLogin ? 'Showing Admin Login' : 'Showing App Downloads'}
            </Typography> */}

            <Divider sx={{ borderColor: 'rgba(16,117,186,0.1)' }} />

            {/* Animated panel swap */}
            <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {showAdminLogin ? <LoginContent /> : <DownloadsContent />}
            </Box>
          </Box>

          {/* ── DESKTOP LAYOUT ── */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row' }}>

            {/* LEFT: Login */}
            <Box sx={{
              flex: 1,
              p: 5,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}>
              <Box sx={{ mb: 4 }}>
                <Box sx={{ width: 56, height: 56, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src="logo.png" style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="Logo" />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f2137', mb: 0.5 }}>
                  Admin Console
                </Typography>
                <Typography variant="body2" sx={{ color: '#4a6a85' }}>
                  Sign in to access the management panel
                </Typography>
              </Box>

              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {error && (
                    <Alert severity="error" sx={{
                      borderRadius: 2,
                      background: 'rgba(244,67,54,0.08)',
                      border: '1px solid rgba(244,67,54,0.25)',
                      color: '#d32f2f',
                      '& .MuiAlert-icon': { color: '#f44336' },
                    }}>
                      {error}
                    </Alert>
                  )}
                  <TextField
                    label="Username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required fullWidth autoFocus autoComplete="username"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: '#4a6a85', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    label="Password"
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: '#4a6a85', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPass(!showPass)} edge="end" size="small">
                            {showPass
                              ? <VisibilityOff sx={{ color: '#4a6a85', fontSize: 20 }} />
                              : <Visibility sx={{ color: '#4a6a85', fontSize: 20 }} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth size="large"
                    disabled={loading}
                    sx={{ mt: 0.5, py: 1.5, fontSize: '1rem' }}
                  >
                    {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign In'}
                  </Button>
                </Box>
              </form>

              <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#6b8fa8' }}>
                Restricted access — authorized administrators only
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgb(114 186 16 / 12%)' }} />

            {/* RIGHT: Downloads */}
            <Box sx={{
              width: 420,
              p: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(160deg, rgba(16,117,186,0.04) 0%, rgba(0,212,170,0.04) 100%)',
              gap: 2,
            }}>
              <DownloadsContent />
            </Box>
          </Box>

        </Card>
      </Box>
    </Box>
  );
}